'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import {
  AuthenticatedTicketSink,
  CallCenterWidget,
  PublicTicketSink,
  withPersistedHistory,
  type EscalationSink,
} from '@its-simple-ai/callcenter-widget';
import { useSidebar } from '@/contexts/SidebarContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { ContextAwareRuntime } from '@/lib/callcenter/context-aware-runtime';
import { extractTicketMetadata } from '@/lib/callcenter/extract-ticket-metadata';
import { useAuthenticatedToken } from '@/lib/callcenter/use-authenticated-token';
import {
  useUserContextProvider,
  type DashboardSessionSummary,
} from '@/lib/callcenter/use-user-context';
import { getWidgetConfig, renderContactHint } from '@/lib/callcenter/widget-config';
import { EVENTO_ABRIR_ASISTENTE, useLanzadorAsistenteOculto } from '@/lib/callcenter/asistente-ia';
import { RuntimeConAsistenteFormato, estaEnPantallaDeFormatos, saludoYSugerencias } from '@/lib/format-templates/asistente-formato';
import { useHayEditorFormato } from '@/lib/format-templates/use-asistente-formato';
import { DEFAULT_LOCALE, type Locale } from '@/lib/i18n';

/**
 * Landing / anonymous visitors — floating bubble. No session yet, so no
 * userRole or plan; we still pass route/pathname so the AI knows which
 * marketing page the visitor is reading. Tickets go through the public
 * unauthenticated endpoint and the contact form asks for email.
 *
 * The landing has its own inline-translations system (`Lang` type), so we
 * take the locale as a prop instead of reading `useLanguage()` — that
 * provider lives in the dashboard tree only.
 */
/**
 * Fire-and-forget: tell EZDocu a support ticket was just opened so the internal
 * team gets an email alert (recipients configured in admin Settings). Safe to
 * call from the public/anonymous widget — the server only ever emails the
 * configured internal list, never a user-supplied address.
 */
function notifyTicketOpened(
  result?: { ticketId: string; ticketNumber: number },
  requester?: { email?: string | null; name?: string | null },
) {
  if (!result?.ticketId) return;
  try {
    void fetch('/api/support/ticket-opened', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ticketId: result.ticketId,
        ticketNumber: result.ticketNumber,
        // Present only for signed-in users; anonymous visitors stay anonymous.
        requesterEmail: requester?.email || undefined,
        requesterName: requester?.name || undefined,
      }),
      keepalive: true,
    }).catch(() => { /* best-effort */ });
  } catch {
    /* ignore */
  }
}

export function CallCenterWidgetLoader({ locale }: { locale?: Locale } = {}) {
  const config = useMemo(() => getWidgetConfig(locale ?? DEFAULT_LOCALE), [locale]);
  const getUserContext = useUserContextProvider(null);
  const sessionId = useSessionId();
  const runtime = useMemo(
    () =>
      new ContextAwareRuntime({
        apiUrl: config.apiUrl,
        product: config.product,
        tenantSlug: config.tenantSlug,
        sessionId,
        language: config.language,
        getUserContext,
      }),
    [config, sessionId, getUserContext],
  );
  const sink = useMemo<EscalationSink>(
    () =>
      new PublicTicketSink({
        apiUrl: config.apiUrl,
        product: config.product,
        tenantSlug: config.tenantSlug,
        getMetadata: () => extractTicketMetadata(getUserContext() ?? null, null),
      }),
    [config, getUserContext],
  );

  return (
    <CallCenterWidget
      apiUrl={config.apiUrl}
      product={config.product}
      tenantSlug={config.tenantSlug}
      language={config.language}
      title={config.title}
      subtitle={config.subtitle}
      brandAvatarUrl={config.brandAvatarUrl}
      greetingHtml={config.greetingHtml}
      composerPlaceholder={config.composerPlaceholder}
      suggestedQuestions={config.suggestedQuestions}
      escalationLabels={config.escalationLabels}
      theme={config.theme}
      variant="floating"
      runtime={runtime}
      sink={sink}
      onTicketCreated={notifyTicketOpened}
    />
  );
}

export interface DashboardCallCenterWidgetProps {
  session?: DashboardSessionSummary | null;
}

/**
 * Docked variant for authenticated dashboards.
 *
 * Three things distinguish this from the floating variant:
 *   1. Tickets post to the authenticated `/tickets` endpoint with a JWT
 *      minted via `/api/callcenter/dev-token`, so the ticket is linked to
 *      the real EZDocu user — no synthetic `widget:email` records.
 *   2. `autoEscalate` skips the email/name form; we already know the user.
 *   3. Chat history is persisted in localStorage per user so reopening
 *      the widget keeps backend context (semantic continuation).
 *
 * Reads the active dashboard locale from `useLanguage()` so copy, suggested
 * prompts and the AI's response language follow whatever the user picked in
 * the sidebar language switcher.
 */
export function DashboardCallCenterWidget({ session }: DashboardCallCenterWidgetProps) {
  const { setWidgetOpen } = useSidebar();
  const { locale, t } = useLanguage();
  const pathname = usePathname();
  // New-order / document processing is a tight two-pane layout; the docked
  // sparkle bubble overlaps the form and preview. Hide it on this flow only.
  const hideOnNewOrder = (pathname || '').includes('/dashboard/orders/new');
  const config = useMemo(() => getWidgetConfig(locale), [locale]);
  const [open, setOpen] = useState(false);
  const getUserContext = useUserContextProvider(session ?? null);
  const sessionId = useSessionId();
  const getToken = useAuthenticatedToken();

  const sessionRef = useRef<DashboardSessionSummary | null>(session ?? null);
  useEffect(() => {
    sessionRef.current = session ?? null;
  }, [session]);

  useEffect(() => {
    if (hideOnNewOrder) {
      setOpen(false);
      setWidgetOpen(false);
      return;
    }
    setWidgetOpen(open);
  }, [hideOnNewOrder, open, setWidgetOpen]);

  const persistKey = session?.userId
    ? `ezcc.history.v1.${session.userId}`
    : null;

  const runtime = useMemo(() => {
    const base = new ContextAwareRuntime({
      apiUrl: config.apiUrl,
      product: config.product,
      tenantSlug: config.tenantSlug,
      sessionId,
      language: config.language,
      getUserContext,
    });
    // Con el editor de Formatos abierto, el asistente ajusta la plantilla; lo demás sigue al callcenter.
    const conFormato = new RuntimeConAsistenteFormato(base);
    return persistKey ? withPersistedHistory(conFormato, persistKey) : conFormato;
  }, [config, sessionId, getUserContext, persistKey]);

  const sink = useMemo<EscalationSink>(
    () =>
      new AuthenticatedTicketSink({
        apiUrl: config.apiUrl,
        getToken,
        language: config.language,
        getMetadata: () =>
          extractTicketMetadata(getUserContext() ?? null, sessionRef.current),
      }),
    [config, getToken, getUserContext],
  );

  const handleTicketCreated = useCallback((result?: { ticketId: string; ticketNumber: number }) => {
    // Alert the internal support team by email (fire-and-forget). Include the
    // signed-in requester so the alert shows who opened it instead of "Anonymous".
    notifyTicketOpened(result, {
      email: sessionRef.current?.userEmail,
      name: sessionRef.current?.userName,
    });
    // Conversation ended — drop the persisted transcript so the next
    // chat starts fresh. The ticket itself carries the history.
    const key = persistKey;
    if (!key || typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }, [persistKey]);

  const contactHint = session?.userEmail
    ? renderContactHint(config.contactHintTemplate, session.userEmail)
    : undefined;

  // Pantallas con su propio botón «Asistente de IA» (editor de Formatos):
  // abren este mismo panel por evento y piden esconder el botón flotante.
  const sinLanzador = useLanzadorAsistenteOculto();
  // Editor de Formatos abierto (y en su pantalla): saludo y sugerencias de formato; si no, los de siempre.
  const conEditorFormato = useHayEditorFormato() && estaEnPantallaDeFormatos(pathname || '');
  const { greetingHtml, suggestedQuestions } = useMemo(
    () => saludoYSugerencias(conEditorFormato, config, t.formatTemplates),
    [conEditorFormato, config, t],
  );
  useEffect(() => {
    if (hideOnNewOrder) return;
    const abrir = () => setOpen(true);
    window.addEventListener(EVENTO_ABRIR_ASISTENTE, abrir);
    return () => window.removeEventListener(EVENTO_ABRIR_ASISTENTE, abrir);
  }, [hideOnNewOrder]);

  if (hideOnNewOrder) return null;

  // El paquete no trae opción para quitar solo el lanzador: su contenedor fijo
  // lleva el panel (<section role="dialog">) y, como hermano, el div del botón
  // redondo. Se esconde ese hermano con CSS, sin tocar el vendor; si el paquete
  // cambia su marcado, el botón simplemente reaparece (nada se rompe).
  return (
    <div className="contents" data-ezcc-sin-lanzador={sinLanzador ? '' : undefined}>
      {sinLanzador && <style>{CSS_SIN_LANZADOR}</style>}
      <CallCenterWidget
        apiUrl={config.apiUrl}
        product={config.product}
        tenantSlug={config.tenantSlug}
        language={config.language}
        title={config.title}
        subtitle={config.subtitle}
        brandAvatarUrl={config.brandAvatarUrl}
        greetingHtml={greetingHtml}
        composerPlaceholder={config.composerPlaceholder}
        suggestedQuestions={suggestedQuestions}
        escalationLabels={config.escalationLabels}
        theme={config.theme}
        variant="docked"
        open={open}
        onOpenChange={setOpen}
        runtime={runtime}
        sink={sink}
        autoEscalate
        escalationContactHint={contactHint}
        onTicketCreated={handleTicketCreated}
      />
    </div>
  );
}

const CSS_SIN_LANZADOR = '[data-ezcc-sin-lanzador] section[role="dialog"] + div { display: none !important; }';

/**
 * Persistent session identifier for the widget's stateless backend. We
 * don't use the authenticated user id here because the widget is public
 * and the backend endpoint is anonymous — mixing the two would leak user
 * ids into pg-boss traces without extra auth value.
 */
function useSessionId(): string {
  const [id, setId] = useState<string>(() => {
    if (typeof window === 'undefined') return placeholderSessionId();
    const existing = window.localStorage.getItem('cc.widget.sessionId');
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    window.localStorage.setItem('cc.widget.sessionId', fresh);
    return fresh;
  });

  // Re-read on first client mount in case SSR returned the placeholder.
  useEffect(() => {
    if (id.startsWith('ssr-')) {
      const existing = window.localStorage.getItem('cc.widget.sessionId');
      if (existing) {
        setId(existing);
      } else {
        const fresh = crypto.randomUUID();
        window.localStorage.setItem('cc.widget.sessionId', fresh);
        setId(fresh);
      }
    }
  }, [id]);

  return id;
}

function placeholderSessionId(): string {
  // Deterministic-ish placeholder; only survives until the client mount
  // hook replaces it with the real UUID from localStorage.
  return `ssr-00000000-0000-0000-0000-000000000000`;
}
